<template>
  <div>
    <div class="flex items-center">
      <div v-if="$roles.isSuperUser && false" class="ml-auto flex justify-end">
        <button
          class="button button-secondary flex items-center text-sm text-black-75%"
          @click="allowSorting = !allowSorting"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1225.3 1225" class="w-3 mr-2 -ml-2 fill-current">
            <path
              d="M494.3 1108a14682.8 14682.8 0 01-117.1-117.9c0-1.3 81.8-82.6 83.1-82.6.7 0 21.2 20 45.6 44.3 24.3 24.4 44.7 44 45.3 43.7 1.2-.8 1.3-151.9.2-252l-.9-72.2h-161c-88.6 0-161 .4-161 .9s20 21 44.6 45.6l44.7 44.7-41.9 41.9c-23 23-42.3 41.7-42.9 41.5-2.1-.7-233.4-232.6-233-233.7.2-.6 53-53.7 117.2-118L234 377.5l42 42 41.8 41.8-44.7 44.7a1587.8 1587.8 0 00-44.7 45.7c0 .5 72.5.9 161 .9h161.1l.9-71.5c1.1-99.1 1-250.7-.2-251.5-.6-.3-20.7 19-44.7 43a1088 1088 0 01-45 43.8 2030 2030 0 01-83-83C382.5 228.6 611.6 0 612.1 0c.4 0 53 52.3 117 116.3l116.2 116.2-42.2 42.2-42.2 42.2-44-44a1537 1537 0 00-45-44.1c-.5 0-.8 72.7-.7 161.5l.4 161.6 162.2.3c89.2.2 162.1-.1 162.1-.6s-20-21-44.3-45.4L907.2 462l41.8-42 41.8-41.9L1108 495.3l117.2 117.2L1109 728.7A7791 7791 0 01991.6 845c-.7 0-20-18.7-42.9-41.6L907.2 762l43.7-43.8a926 926 0 0043.8-45.3c0-1.2-32.4-1.5-161.6-1.2l-161.5.3-.4 162.2c-.1 89.2.2 162.2.7 162.2.5 0 21-20.2 45.6-44.7l44.7-44.7 42.2 42.2 42.2 42.1-117 117A13415 13415 0 01612.2 1225c-.4 0-53.4-52.7-117.8-117z"
            />
          </svg>

          <LocalizeWord v-if="!allowSorting" v-t="'Home.Edit'"></LocalizeWord>
          <LocalizeWord v-if="allowSorting" v-t="'Home.End-edit'"></LocalizeWord>
        </button>
      </div>
    </div>

    <nav class="flex flex-wrap -m-2 mt-4 sm:mt-8 items-stretch">
      <tiles-tile
        v-for="tile in tilesList"
        :key="tile.id"
        ref="tiles"
        :tile="tile"
        @heightRecalculateRequired="onHeightRecalculateRequired"
      ></tiles-tile>
    </nav>
  </div>
</template>

<script>
import LocalizeWord from "@/components/Shared/LocalizeWord";
import { screenWidth } from "@/helpers/utilities/windowHelper";
import Closable from "@/directives/Closable";
import TilesTile from "./TilesTile";

const groupBy = function (xs, key) {
  return xs.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export default {
  name: "DashboardTiles",

  data() {
    return {
      allowSorting: false,
      tileHeight: null,
      removeTileStartedForId: null,
      screenWidth: screenWidth()
    };
  },
  props: {
    tiles: {
      type: Array,
      required: true
    },
    sort: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    window.addEventListener("resize", this.onWindowResize);

    this.$nextTick(() => {
      setTimeout(() => {
        this.setTilesToEqualHeights();
      }, 100);
    });
  },

  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
  },

  watch: {
    tiles() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.setTilesToEqualHeights();
        });
      });
    }
  },

  computed: {
    tilesList() {
      if (this.tiles.filter(tile => tile.gridColumn).length > 0) {
        const tileColumnGroupsMap = groupBy(this.tiles, "gridColumn");
        Object.values(tileColumnGroupsMap).forEach(group => group.sort((a, b) => a.sort - b.sort));
        let longestColumn = 0;
        Object.keys(tileColumnGroupsMap).forEach(key => {
          const highestSortInColumn = tileColumnGroupsMap[key].reduce(
            (acc, tile) => (acc = acc > tile.sort ? acc : tile.sort)
          );
          if (highestSortInColumn > longestColumn) {
            longestColumn = highestSortInColumn;
          }
        });

        let noOfColums = 1;

        switch (this.screenWidth) {
          case "sm": {
            noOfColums = 2;
            break;
          }

          case "md": {
            noOfColums = 3;
            break;
          }

          case "lg": {
            noOfColums = 4;
            break;
          }

          case "xl": {
            noOfColums = 5;
            break;
          }

          default:
            return [...Object.values(tileColumnGroupsMap).flatMap(t => t)];
        }

        const tileListGrouped = [];

        for (
          let columnGroupIdx = 0;
          columnGroupIdx < Math.ceil(Object.values(tileColumnGroupsMap).length / noOfColums);
          columnGroupIdx++
        ) {
          for (let rowIdx = 1; rowIdx <= longestColumn; rowIdx++) {
            for (let columnIdx = 1; columnIdx <= noOfColums; columnIdx++) {
              if (tileColumnGroupsMap[columnIdx + columnGroupIdx * noOfColums]) {
                const tile = tileColumnGroupsMap[columnIdx + columnGroupIdx * noOfColums].find(
                  tile => tile.sort === rowIdx
                );
                if (tile) {
                  tileListGrouped.push(tile);
                } else {
                  tileListGrouped.push({ id: Math.floor(Math.random() * 1000000) });
                }
              } else {
                tileListGrouped.push({ id: Math.floor(Math.random() * 1000000) });
              }
            }
          }
        }

        return tileListGrouped;
      }

      const tileList = this.tiles;

      return tileList
        .sort(function (a, b) {
          return b.sort - a.sort;
        })
        .reverse();
    }
  },

  methods: {
    setTilesToEqualHeights() {
      if (this.$refs.tiles) {
        let heightOfTiles = 0;
        this.$refs.tiles.map(t => (t.$el.style.height = "auto"));
        heightOfTiles = Math.max(...this.$refs.tiles.map(t => t.$el.offsetHeight));
        this.$refs.tiles.map(t => (t.$el.style.height = heightOfTiles + "px"));
      }
    },

    startRemoveTile(tileId) {
      this.removeTileStartedForId = tileId;
    },

    stopRemoveTile() {
      this.removeTileStartedForId = null;
    },

    removeTile(tileId) {
      this.removeTileStartedForId = null;
      this.$emit("removeTile", tileId);
    },

    onHeightRecalculateRequired() {
      this.setTilesToEqualHeights();
    },

    onWindowResize() {
      this.setTilesToEqualHeights();
      this.screenWidth = screenWidth();
    }
  },
  components: {
    LocalizeWord,
    TilesTile
  },

  directives: {
    Closable
  }
};
</script>

<style scoped>
.overflow-ellipsis {
  text-overflow: ellipsis;
}
</style>
